import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import AsyncSelect from 'react-select/async';
import { Card, Badge, EmptyState, TextInput } from '@opendate-app/setlist';
import AdminPageHead from '../../AdminShared/AdminPageHead';
import client from '../../../shared/api/client';
import { offsetDateForBrowser } from '../../../shared/timeZoneLogic';
import { formatCurrency } from '../../../shared/formattingUtils';
import { toastError, errorText } from '../../../shared/toastHelper';
import '../styles/ticketHistory.css';

const MENU_PORTAL = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
  styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
};

const personLabel = (name, email) => name || email || 'Unknown';

const formatDate = (iso) => {
  if (!iso) return '';
  const d = offsetDateForBrowser(iso);
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : '';
};

const holdersFor = (steps) =>
  steps.map((step) => personLabel(step.toName, step.toEmail));

const badgeColor = (kind) => {
  if (kind === 'purchase') return 'gray';
  if (kind === 'TicketTransfers::ResalePlatform') return 'warning';
  return 'info';
};

const matchesQuery = (chain, q) => {
  if (!q) return true;
  const haystack = [
    chain.ticket.barcode,
    ...(chain.ticket.barcodes || []),
    chain.ticket.ticketType,
    ...chain.steps.flatMap((s) => [s.fromName, s.fromEmail, s.toName, s.toEmail]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

const ChainRow = ({ chain }) => {
  const [open, setOpen] = useState(false);
  const holders = holdersFor(chain.steps);
  const transfers = chain.steps.length - 1;

  return (
    <div className={open ? 'th-row th-row-open' : 'th-row'}>
      <button type="button" className="th-row-head" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="th-caret">{open ? '▾' : '▸'}</span>
        <span className="th-ticket">
          <span className="th-barcode">{chain.ticket.currentBarcode || chain.ticket.barcode}</span>
          {chain.ticket.ticketType ? <span className="th-type">{chain.ticket.ticketType}</span> : null}
        </span>
        <span className="th-flow">
          {holders.map((holder, i) => (
            <React.Fragment key={`${holder}-${i}`}>
              {i > 0 ? <span className="th-arrow">→</span> : null}
              <span className={i === holders.length - 1 ? 'th-holder th-holder-current' : 'th-holder'}>{holder}</span>
            </React.Fragment>
          ))}
        </span>
        <span className="th-count">{transfers} transfer{transfers === 1 ? '' : 's'}</span>
      </button>
      {open ? (
        <ol className="th-timeline">
          {chain.steps.map((step, i) => (
            <li className="th-step" key={i}>
              <span className="th-step-dot" />
              <div className="th-step-body">
                <div className="th-step-line">
                  <Badge color={badgeColor(step.kind)}>{step.label}</Badge>
                  <span className="th-step-date">{formatDate(step.occurredAt)}</span>
                </div>
                <div className="th-step-people">
                  {step.fromName || step.fromEmail ? (
                    <>
                      <span className="th-muted">{personLabel(step.fromName, step.fromEmail)}</span>
                      <span className="th-arrow">→</span>
                    </>
                  ) : null}
                  <span className="th-step-to">{personLabel(step.toName, step.toEmail)}</span>
                  {step.toEmail && step.toName ? <span className="th-muted">{step.toEmail}</span> : null}
                </div>
                {step.seatStayedWith ? (
                  <div className="th-step-money">
                    <span className="th-money-item"><span className="th-money-label">Seat stayed with</span>{step.seatStayedWith}</span>
                  </div>
                ) : null}
                {step.seat ? (
                  <div className="th-step-money">
                    <span className="th-money-item"><span className="th-money-label">Seat moved with it</span>{step.seat}</span>
                  </div>
                ) : null}
                {step.money ? (
                  <div className="th-step-money">
                    <span className="th-money-item"><span className="th-money-label">Buyer paid</span>{formatCurrency(step.money.buyerPaid)}</span>
                    <span className="th-money-item"><span className="th-money-label">Seller payout</span>{formatCurrency(step.money.sellerPayout)}</span>
                    <span className="th-money-item"><span className="th-money-label">Venue fee</span>{formatCurrency(step.money.venueFee)}</span>
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
};

ChainRow.propTypes = { chain: PropTypes.object.isRequired };

const TicketHistory = ({ endpoints, event, chains }) => {
  const [query, setQuery] = useState('');

  const loadEvents = (input) =>
    client.get(endpoints.eventSearch, { params: { q: input } })
      .then((res) => res.data)
      .catch((e) => {
        toastError(errorText(e, 'Could not search events.'));
        return [];
      });

  const onPick = (option) => {
    if (!option) return;
    window.location = `${window.location.pathname}?calendar_event_id=${option.value}`;
  };

  const q = query.trim().toLowerCase();
  const visible = useMemo(() => chains.filter((chain) => matchesQuery(chain, q)), [chains, q]);

  const byType = useMemo(() => {
    const counts = {};
    chains.forEach((chain) => {
      const type = chain.ticket.ticketType || 'Unknown';
      counts[type] = (counts[type] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [chains]);

  return (
    <>
      <AdminPageHead
        title="Ticket History"
        subtitle="Trace how a ticket moved from its original buyer through every transfer."
      />

      <Card>
        <div className="th-picker">
          <label className="th-picker-label" htmlFor="th-event">Event</label>
          <AsyncSelect
            inputId="th-event"
            classNamePrefix="th-select"
            placeholder="Search confirmed events by name…"
            cacheOptions
            defaultOptions={false}
            loadOptions={loadEvents}
            value={event ? { value: event.id, label: event.label } : null}
            onChange={onPick}
            {...MENU_PORTAL}
          />
        </div>
      </Card>

      {!event ? (
        <Card>
          <EmptyState size="sm" title="Pick an event" description="Choose an event above to see every transferred ticket's history." />
        </Card>
      ) : chains.length === 0 ? (
        <Card>
          <EmptyState size="sm" title="No transfers for this event" description="No tickets in this event have been transferred yet." />
        </Card>
      ) : (
        <>
        <Card>
          <div className="th-summary-title">Transfers by ticket type</div>
          <div className="th-summary">
            {byType.map(([type, count]) => (
              <div className="th-summary-item" key={type}>
                <span className="th-summary-count">{count}</span>
                <span className="th-summary-type">{type}</span>
              </div>
            ))}
          </div>
        </Card>
        <Card padding="none">
          <div className="th-toolbar">
            <TextInput
              size="sm"
              className="th-search"
              placeholder="Filter by fan, email, or barcode…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <span className="th-toolbar-count">
              {visible.length === chains.length
                ? `${chains.length} transferred ${chains.length === 1 ? 'ticket' : 'tickets'}`
                : `${visible.length} of ${chains.length}`}
            </span>
          </div>
          {visible.length === 0 ? (
            <div className="th-no-match">
              <EmptyState size="sm" title="No matches" description={`Nothing matches “${query.trim()}”.`} />
            </div>
          ) : (
            <div className="th-rows">
              {visible.map((chain) => <ChainRow key={chain.ticket.id} chain={chain} />)}
            </div>
          )}
        </Card>
        </>
      )}
    </>
  );
};

TicketHistory.propTypes = {
  endpoints: PropTypes.shape({ eventSearch: PropTypes.string.isRequired }).isRequired,
  event: PropTypes.shape({ id: PropTypes.number, label: PropTypes.string }),
  chains: PropTypes.arrayOf(PropTypes.object),
};

TicketHistory.defaultProps = { event: null, chains: [] };

export default TicketHistory;
